import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Loader2, AlertCircle, X } from 'lucide-react';
import api from '../lib/api';
import OdinLivingWidget from '../components/odin/OdinLivingWidget';
import { ODIN_DISCLOSURE_KEY } from '../components/odin/OdinDisclosure';
import { OdinMascot } from '../components/odin/OdinMascot';

// ─── Main component ──────────────────────────────────────────────

export default function OdinAIPage() {
  // Feature flag
  const [enabled, setEnabled]         = useState(null);
  const [statusError, setStatusError] = useState(null);

  // External-processing notice (in-widget; reopenable via "AI & privacy")
  const [disclosureOpen, setDisclosureOpen] = useState(() => {
    try { return localStorage.getItem(ODIN_DISCLOSURE_KEY) !== 'seen'; } catch { return true; }
  });
  // Closing records that this notice version was seen; it is not consent to optional processing.
  const closeDisclosure = () => {
    try { localStorage.setItem(ODIN_DISCLOSURE_KEY, 'seen'); } catch {}
    setDisclosureOpen(false);
  };

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo]     = useState(null);

  // Chat / streaming
  const [streaming, setStreaming]         = useState(false);
  const [streamBuffer, setStreamBuffer]   = useState('');
  const [error, setError]                 = useState(null);

  // Usage allowance
  const [usage, setUsage] = useState(null);

  // Attachment upload (Vault integration)
  const [attachFiles, setAttachFiles] = useState([]);

  const inputRef = useRef(null);

  // ─── Feature flag ─────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/odin/status');
        setEnabled(res.data.enabled);
      } catch (err) {
        if (err.response?.status === 404) setEnabled(false);
        else setStatusError('Could not reach oDIN. Try refreshing.');
      }
    })();
  }, []);

  // ─── Conversations ────────────────────────────────────────────

  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get('/odin/conversations');
      setConversations(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (enabled) loadConversations(); }, [enabled, loadConversations]);

  // ─── Usage ───────────────────────────────────────────────────

  const refreshUsage = useCallback(async () => {
    try {
      const res = await api.get('/odin/usage');
      setUsage(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (enabled) refreshUsage(); }, [enabled, refreshUsage]);

  // ─── Conversation ops ────────────────────────────────────────

  const openConversation = useCallback(async (id) => {
    try {
      const res = await api.get(`/odin/conversations/${id}`);
      setActiveConvo(res.data);
      setError(null);
      setStreamBuffer('');
    } catch { setError('Failed to load conversation.'); }
  }, []);

  const newConversation = useCallback(() => {
    setActiveConvo(null);
    setError(null);
    setStreamBuffer('');
  }, []);

  // ─── Send message (streaming SSE) ────────────────────────────

  const sendMessageText = useCallback(async (text) => {
    if (!text || streaming) return;

    setError(null);
    setStreaming(true);
    setStreamBuffer('');

    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setActiveConvo((prev) => {
      if (!prev) return { id: null, messages: [userMsg], title: text.slice(0, 80) };
      return { ...prev, messages: [...(prev.messages || []), userMsg] };
    });

    let fullContent = '';
    let convoId = activeConvo?.id ?? null;
    let hadError = false;
    let receivedDone = false;

    try {
      const res = await fetch(`${api.defaults.baseURL}/odin/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('midgard_token')}`,
        },
        body: JSON.stringify({ message: text, conversation_id: activeConvo?.id || undefined }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.type === 'started') {
              if (payload.conversation_id) {
                convoId = payload.conversation_id;
                setActiveConvo((prev) => {
                  if (prev && !prev.id) return { ...prev, id: payload.conversation_id };
                  return prev;
                });
              }
            } else if (payload.type === 'chunk') {
              fullContent += payload.content;
              setStreamBuffer(fullContent);
              if (payload.conversation_id) convoId = payload.conversation_id;
            } else if (payload.type === 'done') {
              receivedDone = true;
              if (payload.conversation_id) convoId = payload.conversation_id;
            } else if (payload.type === 'error') {
              hadError = true;
              setError(payload.message);
            }
          } catch { /* ignore malformed */ }
        }
      }
    } catch (err) {
      hadError = true;
      setError(err.message || 'Failed to reach oDIN.');
    } finally {
      setStreamBuffer('');
      if (fullContent && !hadError && receivedDone) {
        const asstMsg = { role: 'assistant', content: fullContent, timestamp: new Date().toISOString() };
        setActiveConvo((prev) => {
          if (prev?.id && convoId && prev.id !== convoId) return prev;
          const updated = { ...prev, messages: [...(prev?.messages || []), asstMsg] };
          if (convoId) updated.id = convoId;
          return updated;
        });
      }
      setStreaming(false);
      loadConversations();
      refreshUsage();
    }
  }, [streaming, activeConvo, loadConversations, refreshUsage]);

  // ─── Attach files (Vault upload proxy) ───────────────────────

  const handleAttachFiles = useCallback(async (files) => {
    setAttachFiles(files);
    if (!files.length) return;
    try {
      const form = new FormData();
      form.append('file', files[0]);
      await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch { /* silent — attachment notice shown in widget */ }
  }, []);

  // ─── Derived state (hooks must be before early returns) ──────

  // Include streaming content as the live last message
  const displayMessages = useMemo(() => {
    const base = activeConvo?.messages || [];
    if (streaming && streamBuffer) {
      return [...base, { role: 'assistant', content: streamBuffer, id: '__streaming__' }];
    }
    return base;
  }, [activeConvo?.messages, streaming, streamBuffer]);

  const streamState = streaming && streamBuffer ? 'speaking' : 'idle';
  const hourlyRemaining = usage?.remaining_this_hour ?? null;

  // ─── Early returns ───────────────────────────────────────────

  if (enabled === null && !statusError) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mg-purple" />
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
          <p className="text-muted-foreground">{statusError}</p>
        </div>
      </div>
    );
  }

  if (!enabled) return <OdinDisabledShell />;

  // ─── Render ──────────────────────────────────────────────────

  return (
    <div
      className="mg-stage"
      style={{ display: 'flex', flexDirection: 'column', zIndex: 10 }}
      data-testid="odin-ai-page"
    >
      {/* Error toast */}
      {error && (
        <div style={{
          position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100% - 24px)',
          zIndex: 30, background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)',
          color: '#fca5a5', borderRadius: 10, padding: '8px 16px',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
          backdropFilter: 'blur(4px)',
        }}>
          <AlertCircle style={{ width: 15, height: 15, flexShrink: 0 }} />
          {error}
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fca5a5', padding: 0 }}>
            <X style={{ width: 13, height: 13 }} />
          </button>
        </div>
      )}

      {/* Living widget — fills the available space */}
      <OdinLivingWidget
        conversations={conversations}
        messages={displayMessages}
        activeConversationId={activeConvo?.id ?? null}
        onCreateConversation={newConversation}
        onSelectConversation={openConversation}
        onSendMessage={sendMessageText}
        onOpenLibrary={() => {/* Vault handled inside widget sidebar */}}
        onAttachFiles={handleAttachFiles}
        onOpenWorkspace={() => {/* no-op: workspace sections live inside widget */}}
        busy={streaming && !streamBuffer}
        streamState={streamState}
        hourlyRemaining={hourlyRemaining}
        disclosureOpen={disclosureOpen}
        onDisclosureClose={closeDisclosure}
        onDisclosureOpen={() => setDisclosureOpen(true)}
      />
    </div>
  );
}

// ─── Disabled shell ──────────────────────────────────────────────

function OdinDisabledShell() {
  return (
    <div className="px-4" data-testid="odin-ai-page">
      <div className="max-w-3xl mx-auto py-8">
        <div className="mg-card-elevated p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 mg-slot-grad opacity-40 pointer-events-none" />
          <div className="relative">
            <div className="flex justify-center">
              <OdinMascot state="idle" size={80} />
            </div>
            <h1 className="font-heading text-4xl font-bold mt-5">
              oDIN <span className="text-mg-purple">AI</span>
            </h1>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              The intelligence woven through Midgard — your guide across posts, profiles,
              search, communities, creation and the game world.
            </p>
            <span className="inline-block mt-4 text-[10px] uppercase tracking-widest font-bold
                             px-3 py-1 rounded-full bg-mg-purple/15 text-mg-purple border border-mg-rune/30">
              Coming Soon
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
